import { useCallback, useEffect, useRef, useState } from 'react';

/** One request at a time per selection. Background tabs never poll. */
export function useVisiblePolling<T>(load: () => Promise<T>, interval = 15_000) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const pollRef = useRef<() => void>(() => {});
  const refresh = useCallback(() => pollRef.current(), []);
  useEffect(() => {
    setData(null);
    setError('');
  }, [load]);
  useEffect(() => {
    let alive = true,
      pending = false;
    const poll = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const next = await load();
        if (alive) {
          setData(next);
          setError('');
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Не удалось обновить состояние');
      } finally {
        pending = false;
      }
    };
    void poll();
    pollRef.current = () => void poll();
    const timer = window.setInterval(() => void poll(), interval);
    const wake = () => void poll();
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return () => {
      alive = false;
      pollRef.current = () => {};
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [load, interval]);
  return { data, error, refresh };
}
